import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  LinearProgress,
  Snackbar,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import ClipboardJS from "clipboard";
import { AppVarsContext } from "../../context/AppVarsContext";
import StyleGuides from "../style-guides";

const decodePreviewToken = (token) => {
  if (!token) {
    return "";
  }

  try {
    return decodeURIComponent(atob(token));
  } catch (error) {
    try {
      return decodeURIComponent(token);
    } catch (decodeError) {
      return token;
    }
  }
};

const formatTimestamp = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toUTCString();
};

const formatPercent = (value, total) => {
  if (!total) {
    return "0.00%";
  }

  return `${((value / total) * 100).toFixed(2)}%`;
};

const LightboxPreviewc = () => {
  const { pid = "" } = useParams();
  const { appVars } = useContext(AppVarsContext);
  const [lightbox, setLightbox] = useState(null);
  const [stats, setStats] = useState({ impressions: 0, clicked: 0, closed: 0 });
  const [copySuccessOpen, setCopySuccessOpen] = useState(false);
  const [errors, setErrors] = useState({ open: false, msg: "" });

  const decodedPid = decodePreviewToken(pid);
  const [lightboxId] = decodedPid.split("|");

  useEffect(() => {
    if (!appVars || !lightboxId) {
      if (!lightboxId && pid) {
        setErrors({ open: true, msg: "Invalid preview link." });
      }
      return undefined;
    }

    let isActive = true;

    const fetchData = async () => {
      try {
        const response = await fetch(`${appVars.api_url}/lightbox/${lightboxId}`);
        if (!response.ok) {
          throw new Error(`Failed to load lightbox ${lightboxId}: ${response.status}`);
        }

        const lightboxData = await response.json();
        if (!isActive) {
          return;
        }

        setLightbox(lightboxData);
        document.title = lightboxData.description || document.title;

        document
          .querySelectorAll('script[data-preview-type="c"]')
          .forEach((script) => script.remove());

        const previewScript = document.createElement("script");
        previewScript.src = `${appVars.api_url}/scripts/html-preview.js`;
        previewScript.async = true;
        previewScript.id = `${lightboxId}|${lightboxData.beaconId}`;
        previewScript.className = "dwi-components";
        previewScript.dataset.previewType = "c";
        document.body.appendChild(previewScript);

        const start = new Date(lightboxData.startTime);
        const end = new Date(lightboxData.endTime);
        const statsParams = new URLSearchParams({
          module: "API",
          format: "JSON",
          idSite: String(lightboxData.beaconId),
          period: "range",
          date: `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()},${end.getFullYear()}-${end.getMonth() + 1}-${end.getDate()}`,
          method: "Events.getName",
          expanded: "1",
          pivotBy: "Events.EventAction",
          pivotByColumnLimit: "20",
          pivotByColumn: "nb_events",
          token_auth: appVars.matomo_token,
          filter_limit: "50",
        });

        const statsResponse = await fetch(`${appVars.matomo_url}/index.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: statsParams.toString(),
        });

        if (!statsResponse.ok) {
          throw new Error(`Failed to load stats for ${lightboxId}: ${statsResponse.status}`);
        }

        const statsJson = await statsResponse.json();
        if (!Array.isArray(statsJson) || statsJson.length === 0 || !isActive) {
          setStats({ impressions: 0, clicked: 0, closed: 0 });
          return;
        }

        const targetStats =
          statsJson.find((entry) => entry.label === lightboxData.lightboxId) || statsJson[0];
        const values = Object.values(targetStats || {});

        setStats({
          impressions: Number(values[1]) || 0,
          closed: Number(values[2]) || 0,
          clicked: Number(values[3]) || 0,
        });
      } catch (error) {
        if (isActive) {
          setErrors({ open: true, msg: "Error fetching preview data." });
        }
        console.error(error);
      }
    };

    fetchData();

    return () => {
      isActive = false;
      document
        .querySelectorAll('script[data-preview-type="c"]')
        .forEach((script) => script.remove());
    };
  }, [appVars, lightboxId, pid]);

  useEffect(() => {
    const clipboard = new ClipboardJS("#copy-script");

    clipboard.on("success", (event) => {
      setCopySuccessOpen(true);
      event.clearSelection();
    });

    return () => clipboard.destroy();
  }, []);

  const handleClose = () => {
    setCopySuccessOpen(false);
    setErrors({ open: false, msg: "" });
  };

  if (!lightbox) {
    return (
      <Box p={10}>
        <LinearProgress />
      </Box>
    );
  }

  const impressions = stats.impressions;
  const clicked = stats.clicked;
  const closed = stats.closed;
  const installScript = `<script class="dwi-components" id="${lightbox.lightboxId}|${lightbox.beaconId}" src="${appVars?.api_url || "https://api.digitalwerks.com"}/scripts/h.js"></script>`;

  return (
    <Box
      className="hide-preview"
      sx={{
        backgroundColor: "ghostwhite",
        minHeight: "100vh",
        p: { xs: 2, md: 4 },
      }}
    >
      <Typography align="center" color="secondary" variant="h6">
        For Lightbox Preview Only.
        <br />
        Close this window after Preview.
      </Typography>
      <Typography align="center" color="secondary" variant="body2">
        * Date/Display Settings ignored on this preview page.
      </Typography>

      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, md: 4, xl: 3 }}>
          <Card sx={{ minWidth: 275 }}>
            <CardHeader title={lightbox.description || "Lightbox preview"} />
            <CardContent>
              <Typography variant="body2">
                Start Date -{" "}
                <Box component="span" sx={{ color: "error.main", fontWeight: "bold" }}>
                  {formatTimestamp(lightbox.startTime)}
                </Box>
              </Typography>
              <Typography variant="body2">
                End Date -{" "}
                <Box component="span" sx={{ color: "error.main", fontWeight: "bold" }}>
                  {formatTimestamp(lightbox.endTime)}
                </Box>
              </Typography>
              <Typography variant="body2">
                Display Every{" "}
                <Box
                  component="span"
                  sx={{ color: "error.main", fontWeight: "bold", fontSize: "1em" }}
                >
                  {lightbox.cookieExpire}
                </Box>{" "}
                days
              </Typography>

              <Grid container spacing={1} sx={{ mt: 2 }}>
                <Grid size={4}>
                  <Typography align="center">
                    Impressions:
                    <br />
                    {impressions}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography align="center">
                    Clicked: {formatPercent(clicked, impressions)}
                    <br />
                    {clicked}
                  </Typography>
                </Grid>
                <Grid size={4}>
                  <Typography align="center">
                    Closed: {formatPercent(closed, impressions)}
                    <br />
                    {closed}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
            <CardActions>
              <Button
                color="primary"
                fullWidth
                href={lightbox.url || undefined}
                size="small"
                target="_blank"
                variant="outlined"
              >
                Destination URL
              </Button>
            </CardActions>
          </Card>

          <Card sx={{ minWidth: 275, mt: 2 }}>
            <CardHeader title="Install Instructions" />
            <CardContent>
              <Typography variant="body2">
                To activate the lightbox place the code in the{" "}
                <Box component="span" sx={{ color: "error.main", fontWeight: "bold" }}>
                  &lt;head&gt;
                </Box>{" "}
                tag of the pages you would like the lightbox to show. The lightbox is
                restricted by the setting &apos;restrictto&apos; below in the script tag. This
                lightbox will only show on URLs containing the value of that setting. Remove
                it for all unrestricted display.
              </Typography>
              <Box
                component="pre"
                id="script-text"
                sx={{
                  background: "#272822",
                  borderRadius: "10px",
                  color: "#fff",
                  fontSize: "1rem",
                  mt: 2,
                  p: 2,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {installScript}
              </Box>
            </CardContent>
            <CardActions>
              <Button
                autoFocus
                color="primary"
                data-clipboard-target="#script-text"
                fullWidth
                id="copy-script"
                variant="outlined"
              >
                Copy to Clipboard
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8, xl: 9 }}>
          <StyleGuides />
        </Grid>
      </Grid>

      <Snackbar autoHideDuration={1500} onClose={handleClose} open={copySuccessOpen}>
        <Alert onClose={handleClose} severity="success">
          Script copied to clipboard.
        </Alert>
      </Snackbar>

      <Snackbar autoHideDuration={3000} onClose={handleClose} open={errors.open}>
        <Alert onClose={handleClose} severity="error">
          {errors.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LightboxPreviewc;
